'use client';

import { useState, type ReactNode } from 'react';
import Image from '@/components/ui/Image';
import {
  BankIcon,
  CaretDownIcon,
  ChartLineUpIcon,
  CheckCircleIcon,
  CircleNotchIcon,
  ClockIcon,
  CreditCardIcon,
  PiggyBankIcon,
  ShieldCheckIcon,
  ShieldWarningIcon,
  TrashIcon,
  WarningIcon,
} from '@phosphor-icons/react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tooltip } from '@/components/ui/tooltip';
import { useFormatCurrency } from '@/hooks/useFormatCurrency';
import { getPluggyProductLabel } from '@/lib/pluggy-products';
import type { Account } from '@/types';

export interface ConnectionCardItem {
  id: string;
  pluggyItemId: string;
  connectorId?: number;
  connectorName: string;
  connectorImage: string | null;
  connectorPrimaryColor?: string | null;
  status?: string;
  updatedAt?: string;
  lastUpdatedAt?: string;
  nextAutoSyncAt?: string;
  consentExpiresAt?: string;
  errorCode?: string;
  errorMessage?: string;
  products: string[];
  accounts: Account[];
  connectorHealth?: {
    status: string;
    reason?: string | null;
    selectable?: boolean;
  } | null;
  connectionIssue?: {
    level: string;
    kind: string;
    title: string;
    message: string;
    blockedActions: string[];
  } | null;
}

interface Props {
  item: ConnectionCardItem;
  onReauth: () => void;
  onRenew: () => void;
  onUpdate: () => void;
  onDisconnect: () => void;
}

type ConsentStatus = 'ok' | 'expiring' | 'expired' | null;

function getConsentStatus(consentExpiresAt?: string): ConsentStatus {
  if (!consentExpiresAt) return null;
  const expiresAt = new Date(consentExpiresAt).getTime();
  const now = Date.now();
  if (expiresAt < now) return 'expired';
  if (expiresAt - now < 7 * 24 * 60 * 60 * 1000) return 'expiring';
  return 'ok';
}

function formatRelativeDate(dateStr?: string): string | null {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  const diffMs = date.getTime() - Date.now();
  const absDiffMs = Math.abs(diffMs);
  const isFuture = diffMs > 0;
  const diffMin = Math.floor(absDiffMs / 60000);
  if (diffMin < 1) return isFuture ? 'em instantes' : 'agora';
  if (diffMin < 60) return isFuture ? `em ${diffMin} min` : `há ${diffMin} min`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return isFuture ? `em ${diffH}h` : `há ${diffH}h`;
  const diffD = Math.floor(diffH / 24);
  return isFuture ? `em ${diffD}d` : `há ${diffD}d`;
}

interface StatusPillConfig {
  label: string;
  className: string;
  icon: ReactNode;
}

function getStatusPill(status?: string): StatusPillConfig | null {
  if (!status) return null;
  switch (status) {
    case 'UPDATED':
      return {
        label: 'Atualizado',
        className: 'bg-success-surface text-success-text border-success-border',
        icon: <CheckCircleIcon size={11} weight="fill" />,
      };
    case 'UPDATING':
    case 'MERGING':
      return {
        label: status === 'UPDATING' ? 'Atualizando' : 'Processando',
        className: 'bg-warning-surface text-warning-text border-warning-border',
        icon: <CircleNotchIcon size={11} className="animate-spin" />,
      };
    case 'WAITING_USER_INPUT':
    case 'WAITING_USER_ACTION':
      return {
        label: 'Ação necessária',
        className: 'bg-warning-surface text-warning-text border-warning-border',
        icon: <WarningIcon size={11} weight="fill" />,
      };
    case 'LOGIN_ERROR':
      return {
        label: 'Erro',
        className: 'bg-error-surface text-error-text border-error-border',
        icon: <ShieldWarningIcon size={11} weight="fill" />,
      };
    case 'OUTDATED':
      return {
        label: 'Desatualizado',
        className: 'bg-warning-surface text-warning-text border-warning-border',
        icon: <ClockIcon size={11} weight="fill" />,
      };
    default:
      return null;
  }
}

const ACCOUNT_LABELS: Record<string, string> = {
  BANK: 'Conta corrente',
  CREDIT: 'Cartão de crédito',
  SAVINGS: 'Poupança',
  INVESTMENT: 'Investimentos',
};

function accountTone(type: string) {
  switch (type) {
    case 'BANK':
      return { tone: 'bg-info-surface text-info-text', Icon: BankIcon };
    case 'CREDIT':
      return { tone: 'bg-error-surface text-error-text', Icon: CreditCardIcon };
    case 'SAVINGS':
      return { tone: 'bg-warning-surface text-warning-text', Icon: PiggyBankIcon };
    case 'INVESTMENT':
      return { tone: 'bg-success-surface text-success-text', Icon: ChartLineUpIcon };
    default:
      return { tone: 'bg-surface-sunken text-text-tertiary', Icon: BankIcon };
  }
}

export function ConnectionCard({ item, onReauth, onRenew, onUpdate, onDisconnect }: Props) {
  const formatCurrency = useFormatCurrency();
  const [expanded, setExpanded] = useState(false);
  const statusPill = getStatusPill(item.status);
  const consent = getConsentStatus(item.consentExpiresAt);
  const lastUpdated = formatRelativeDate(item.lastUpdatedAt ?? item.updatedAt);
  const nextSync = formatRelativeDate(item.nextAutoSyncAt);
  const hasLoginError = item.status === 'LOGIN_ERROR';
  const issue = item.connectionIssue;
  const blocksUpdates = issue?.blockedActions.some((action) =>
    action === 'reauth' || action === 'renew' || action === 'update') ?? false;
  const issueTone = issue?.level === 'critical' ? 'rose' : 'amber';
  const issueActionLabel = blocksUpdates ? 'Aguardar normalização' : 'Tentar atualizar';

  const bankBalance = item.accounts
    .filter((a) => a.type !== 'CREDIT')
    .reduce((s, a) => s + a.balance, 0);

  return (
    <Card variant="flat" className="overflow-hidden rounded-[var(--radius-3xl)] border-border bg-surface p-0 shadow-[var(--shadow-1)]">
      <div className="relative border-b border-divider p-4 sm:px-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            {item.connectorImage ? (
              <div className="flex size-12 shrink-0 items-center justify-center rounded-[var(--radius-lg)] border border-border bg-surface p-1.5">
                <Image
                  src={item.connectorImage}
                  alt={item.connectorName}
                  width={36}
                  height={36}
                  className="object-contain"
                  unoptimized
                />
              </div>
            ) : (
              <div className="flex size-12 shrink-0 items-center justify-center rounded-[var(--radius-lg)] bg-surface-sunken text-text-tertiary">
                <BankIcon size={22} weight="bold" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <h3 className="truncate text-base font-semibold text-text-primary">{item.connectorName}</h3>
              <p className="mt-0.5 text-xs font-medium text-text-secondary">
                {item.accounts.length} {item.accounts.length === 1 ? 'conta' : 'contas'}
                {bankBalance !== 0 && (
                  <>
                    {' • '}
                    <span className="font-mono font-bold text-text-primary tabular-nums">{formatCurrency(bankBalance)}</span>
                  </>
                )}
              </p>
            </div>
          </div>
          {statusPill && (
            <span
              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-2xs font-bold ${statusPill.className}`}
            >
              {statusPill.icon}
              {statusPill.label}
            </span>
          )}
        </div>
      </div>

      {/* Attention strip */}
      {issue && (
        <AttentionStrip
          tone={issueTone}
          icon={issue.level === 'critical' ? <ShieldWarningIcon size={16} weight="fill" /> : <WarningIcon size={16} weight="fill" />}
          message={issue.message}
          actionLabel={issueActionLabel}
          onAction={blocksUpdates ? undefined : onUpdate}
          disabled={blocksUpdates}
        />
      )}
      {!blocksUpdates && consent === 'expired' && (
        <AttentionStrip
          tone="rose"
          icon={<ShieldWarningIcon size={16} weight="fill" />}
          message="Consentimento Open Finance expirado"
          actionLabel="Re-autorizar"
          onAction={onReauth}
        />
      )}
      {!blocksUpdates && consent === 'expiring' && (
        <AttentionStrip
          tone="amber"
          icon={<ClockIcon size={16} weight="fill" />}
          message={`Consentimento expira ${formatRelativeDate(item.consentExpiresAt) ?? 'em breve'}`}
          actionLabel="Renovar"
          onAction={onRenew}
        />
      )}
      {!blocksUpdates && hasLoginError && (
        <AttentionStrip
          tone="rose"
          icon={<WarningIcon size={16} weight="fill" />}
          message={item.errorMessage || item.errorCode || 'Erro de conexão com a instituição'}
          actionLabel="Reconectar"
          onAction={onReauth}
        />
      )}

      <div className={expanded ? '' : 'hidden sm:block'}>
        {/* Meta row: last sync + next sync + actions */}
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-divider bg-surface-sunken px-4 py-2.5 sm:px-5">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-text-secondary">
            {lastUpdated && (
              <span className="inline-flex items-center gap-1.5">
                <CheckCircleIcon size={12} weight="fill" className="text-success" />
                Atualizado {lastUpdated}
              </span>
            )}
            {nextSync && (
              <span className="inline-flex items-center gap-1.5">
                <ClockIcon size={12} />
                Próxima sincronização {nextSync}
              </span>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            <Tooltip content="Desconectar conta">
              <Button
                variant="outline"
                size="icon-sm"
                aria-label="Desconectar conta"
                onClick={onDisconnect}
                className="size-7 rounded-full text-text-tertiary hover:border-error-border hover:text-error"
              >
                <TrashIcon size={14} />
              </Button>
            </Tooltip>
          </div>
        </div>

        {item.products.length > 0 && <PermissionsSection products={item.products} />}

        {/* Accounts */}
        {item.accounts.length === 0 ? (
          <div className="px-4 py-6 text-center text-sm text-text-secondary">Aguardando dados da instituição.</div>
        ) : (
          <ul className="divide-y divide-divider">
            {item.accounts.map((account) => {
              const { tone, Icon } = accountTone(account.type);
              return (
                <li key={account.id} className="flex items-center gap-3 px-4 py-3 sm:px-5">
                  <div className={`flex size-8 shrink-0 items-center justify-center rounded-lg ${tone}`}>
                    <Icon size={16} weight="bold" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-text-primary">
                      {account.name || item.connectorName}
                    </p>
                    <p className="text-xs text-text-secondary">
                      {ACCOUNT_LABELS[account.type] || account.type}
                      {account.number && (
                        <span className="ml-1.5 text-text-tertiary">
                          •••• {account.number.replace(/\D/g, '').slice(-4)}
                        </span>
                      )}
                    </p>
                  </div>
                  <p
                    className={`shrink-0 font-mono text-sm font-bold tabular-nums ${
                      account.balance >= 0 ? 'text-text-primary' : 'text-amount-negative'
                    }`}
                  >
                    {formatCurrency(account.balance, { currency: account.currencyCode })}
                  </p>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        className="flex w-full items-center justify-center gap-1.5 border-t border-divider px-4 py-2 text-2xs font-semibold text-text-secondary transition-colors hover:bg-surface-sunken sm:hidden"
      >
        {expanded ? 'Ocultar contas' : `Ver ${item.accounts.length === 1 ? 'conta' : 'contas'}`}
        <CaretDownIcon
          size={11}
          weight="bold"
          className={`text-text-tertiary transition-transform ${expanded ? 'rotate-180' : ''}`}
        />
      </button>
    </Card>
  );
}

function PermissionsSection({ products }: { products: string[] }) {
  const [open, setOpen] = useState(false);
  const count = products.length;
  return (
    <div className="border-b border-divider">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-2 px-4 py-2.5 text-left transition-colors hover:bg-surface-sunken sm:px-5"
      >
        <span className="inline-flex items-center gap-1.5 text-2xs font-semibold text-text-secondary">
          <ShieldCheckIcon size={12} weight="fill" className="text-success" />
          {count} {count === 1 ? 'permissão autorizada' : 'permissões autorizadas'}
        </span>
        <CaretDownIcon
          size={12}
          weight="bold"
          className={`text-text-tertiary transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div className="flex flex-wrap gap-1.5 px-4 pb-3 sm:px-5">
          {products.map((p) => (
            <span
              key={p}
              className="rounded-md border border-success-border bg-success-surface px-2 py-0.5 text-2xs font-semibold text-success-text"
            >
              {getPluggyProductLabel(p)}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function AttentionStrip({
  tone,
  icon,
  message,
  actionLabel,
  onAction,
  disabled,
}: {
  tone: 'amber' | 'rose';
  icon: ReactNode;
  message: string;
  actionLabel: string;
  onAction?: () => void;
  disabled?: boolean;
}) {
  const cfg = {
    amber: {
      bg: 'bg-warning-surface',
      border: 'border-warning',
      text: 'text-warning-text',
      btn: 'border-warning-border text-warning-text hover:bg-warning-surface',
    },
    rose: {
      bg: 'bg-error-surface',
      border: 'border-error',
      text: 'text-error-text',
      btn: 'border-error-border text-error-text hover:bg-error-surface',
    },
  }[tone];

  return (
    <div className={`flex flex-wrap items-center justify-between gap-2 border-l-4 ${cfg.bg} ${cfg.border} px-4 py-2.5 sm:px-5`}>
      <div className={`flex min-w-0 items-start gap-2 text-xs font-medium ${cfg.text}`}>
        <span className="shrink-0">{icon}</span>
        <span className="min-w-0 break-words">{message}</span>
      </div>
      <Button
        variant="outline"
        size="sm"
        onClick={onAction}
        disabled={disabled}
        className={`h-7 bg-surface text-xs ${cfg.btn}`}
      >
        {actionLabel}
      </Button>
    </div>
  );
}
